/* custom (Second Edition), owner 2026-09-25: Similar Products scans the whole catalog, not only the first
   page. sections/product-recommendations.liquid renders one page of 250 products with each match marked
   data-se-pass (1 same type and pre-order status, 2 same type, 3 same furniture group) and, since
   2026-09-28, a ranking score (data-se-score) and brand (data-se-vendor). This script fetches every other
   page through the section rendering API, merges the matches by score, caps each brand at two and trims to
   the section's product limit. product-recommendations.js later swaps the section's inner HTML, so the merge
   runs again whenever the grid is replaced. */
(() => {
  'use strict';
  if (window.seSimilarProducts) return;
  window.seSimilarProducts = true;

  const cache = new Map();

  function slidesFrom(root) {
    return Array.from(root.querySelectorAll('[data-se-similar] > [data-se-pass]'));
  }

  function fetchPage(url, page) {
    const key = url + '|' + page;
    if (!cache.has(key)) {
      const sep = url.indexOf('?') === -1 ? '?' : '&';
      cache.set(key, fetch(url + sep + 'page=' + page, { credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          return slidesFrom(html);
        })
        .catch(() => []));
    }
    return cache.get(key);
  }

  // Shared with the cart upsell (assets/custom-cart.js, 2026-09-29) as window.seSimilarRank, so both surfaces rank
  // the same way.
  function rank(slides, limit) {
    // Owner 2026-09-28: best score first (match level, then price closeness, then shared details; see
    // sections/product-recommendations.liquid), at most two cards per brand. If the brand cap leaves the row
    // short, the next best cards fill it regardless of brand.
    const MAX_PER_BRAND = 2;
    const seen = new Set();
    const ranked = slides
      .map((slide, index) => ({ slide, index, score: Number(slide.dataset.seScore) || (Number(slide.dataset.sePass) || 9) * 100000 + 99999 }))
      .sort((a, b) => a.score - b.score || a.index - b.index)
      .filter(({ slide }) => {
        // Twin listings (two identical chairs sold separately) share a title; show each title once.
        const handle = slide.dataset.seHandle;
        const titleEl = slide.querySelector('.product-card-title');
        const title = titleEl ? titleEl.textContent.trim().toLowerCase() : '';
        if (!handle || seen.has(handle) || (title && seen.has('t:' + title))) return false;
        seen.add(handle);
        if (title) seen.add('t:' + title);
        return true;
      });
    const brands = new Map();
    const best = [];
    const spill = [];
    ranked.forEach(({ slide }) => {
      const brand = (slide.dataset.seVendor || '').toLowerCase();
      const n = brands.get(brand) || 0;
      if (best.length < limit && (!brand || n < MAX_PER_BRAND)) { best.push(slide); brands.set(brand, n + 1); }
      else spill.push(slide);
    });
    while (best.length < limit && spill.length) best.push(spill.shift());
    return best;
  }
  window.seSimilarRank = rank;
  window.seSimilarFetchPage = fetchPage;
  window.seSimilarSlidesFrom = slidesFrom;

  async function merge(grid) {
    if (grid.dataset.seMerged === 'true' || grid.dataset.seMerging === 'true') return;
    grid.dataset.seMerging = 'true';
    const limit = Number(grid.dataset.seLimit) || 6;
    const pages = Number(grid.dataset.sePages) || 1;
    let slides = slidesFrom(grid.parentElement || grid);
    if (pages > 1 && grid.dataset.seUrl) {
      const others = await Promise.all(Array.from({ length: pages - 1 }, (_, i) => fetchPage(grid.dataset.seUrl, i + 2)));
      others.forEach((list) => { slides = slides.concat(list); });
    }
    const best = rank(slides, limit);
    grid.replaceChildren(...best);
    grid.dataset.seMerged = 'true';
    delete grid.dataset.seMerging;
    const section = grid.closest('product-recommendations');
    if (section && !best.length) section.hidden = true;
    grid.querySelectorAll('.lazyload').forEach((image) => {
      if (window.lazySizes) window.lazySizes.loader.unveil(image);
    });
  }

  function run() {
    document.querySelectorAll('[data-se-similar]').forEach((grid) => { merge(grid); });
  }

  function init() {
    run();
    document.querySelectorAll('product-recommendations').forEach((element) => {
      new MutationObserver(run).observe(element, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
